// src/layouts/Header.jsx

import React, { useEffect } from "react";
import { Toolbar, IconButton, Stack, Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { handleLogin } from "../../services/authService";
import { TbMenu2 } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LogoButton } from "./LogoButton";
import { AuthUserButton } from "./AuthUserButton";
import { HelpMenu } from "./HelpMenu";
import { LanguageSelection } from "./LanguageSelection";
import GlobalSearch from "../../components/common/GlobalSearch";
import { getDashboardUrl, getDashboardLabel } from "../../utils/navigationHelper";

const Header = ({ onToggleSidebar }) => {
  const { t } = useTranslation(['general', 'common']);
  const theme = useTheme();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const [isStandalone, setIsStandalone] = React.useState(false);

  // Detect standalone mode
  useEffect(() => {
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
      setIsStandalone(standalone);
      
      // Update body class for CSS targeting
      if (standalone) {
        document.body.classList.add('pwa-standalone');
      } else {
        document.body.classList.remove('pwa-standalone');
      }
    };
    
    checkStandalone();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkStandalone();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Handle scroll to hide/show header in standalone mode
  useEffect(() => {
    if (!isStandalone) return;
    
    let timeoutId;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Clear previous timeout
      clearTimeout(timeoutId);
      
      // Show header at top of page
      if (currentScrollY < 50) {
        setIsVisible(true);
      } 
      // Hide when scrolling down, show when scrolling up
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      
      // Show header after scroll stops for 2 seconds
      timeoutId = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [isStandalone, lastScrollY]);

  // No need to handle redirect here - it's handled once in index.js
  // Just check if we have an active account
  useEffect(() => {
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount && instance.getAllAccounts().length > 0) {
      console.log("No active account set, setting first account");
      instance.setActiveAccount(instance.getAllAccounts()[0]);
    }
  }, [instance]);

  return (
    <Toolbar
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        minHeight: {
          xs: theme.components.MuiLayout.defaultProps.headerHeight.xs,
          md: theme.components.MuiLayout.defaultProps.headerHeight.md
        },
        // Smooth transitions
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-height 0.3s ease',
        // Hide header when scrolling in standalone mode
        transform: isStandalone && !isVisible ? 'translateY(-100%)' : 'translateY(0)',
        // Prevent layout shift
        willChange: 'transform',
        // Better z-index management
        zIndex: theme.components.MuiLayout.defaultProps.zIndex.header,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 1.5, sm: 3, md: 4 },
        // iOS PWA safe area support - extend header to fill safe area
        '@media (display-mode: standalone)': {
          paddingTop: 'max(env(safe-area-inset-top), 20px)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          minHeight: {
            xs: `calc(${theme.components.MuiLayout.defaultProps.headerHeight.xs}px + max(env(safe-area-inset-top), 20px))`,
            md: `calc(${theme.components.MuiLayout.defaultProps.headerHeight.md}px + max(env(safe-area-inset-top), 20px))`
          },
          // Extend background to fill the entire header area including safe area
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-100px', // Extend above to cover safe area
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: -1
          }
        },
        // Android adjustments
        '@media (display-mode: standalone) and (orientation: portrait)': {
          paddingTop: 'max(env(safe-area-inset-top), 10px)',
          minHeight: {
            xs: `calc(${theme.components.MuiLayout.defaultProps.headerHeight.xs}px + max(env(safe-area-inset-top), 10px))`,
            md: `calc(${theme.components.MuiLayout.defaultProps.headerHeight.md}px + max(env(safe-area-inset-top), 10px))`
          }
        }
      }}
    >
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ 
          width: "100%",
          maxWidth: 'lg',
          mx: 'auto'
        }}
      >
        <Stack 
          direction="row" 
          spacing={0} 
          alignItems="center"
        >
          <IconButton
            onClick={onToggleSidebar}
            sx={{ 
              display: { xs: "flex", md: "none" },
              color: 'text.primary',
              padding: { xs: '6px', sm: '8px' },
              mr: { xs: 1, sm: 2 },
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <TbMenu2 size={24} />
          </IconButton>
          
          <LogoButton />
        </Stack>

        {/* Center Search - Only show on authenticated and larger screens */}
        {isAuthenticated && (
          <Box sx={{ 
            display: { xs: 'none', lg: 'flex' }, 
            flex: 1, 
            justifyContent: 'center',
            maxWidth: 400,
            mx: 2
          }}>
            <GlobalSearch />
          </Box>
        )}

        <Stack 
          direction="row" 
          spacing={{ xs: 0.75, sm: 1.5 }} 
          alignItems="center"
          flexShrink={0}
        >
          {/* Core Navigation Items */}
          {isAuthenticated && (
            <Button
              component={Link}
              to={getDashboardUrl()}
              variant="text"
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: 'text.primary',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 0.5, sm: 1 },
                px: { xs: 1, sm: 1.5 },
                minWidth: { xs: 'auto', sm: '80px' },
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              {getDashboardLabel(t)}
            </Button>
          )}
          
          <HelpMenu 
            sx={{
              '& .MuiIconButton-root': {
                color: 'text.primary',
                padding: { xs: '6px', sm: '8px' }
              }
            }}
          />
          <LanguageSelection 
            sx={{ 
              display: 'flex',
              '& .MuiIconButton-root': {
                padding: { xs: '6px', sm: '8px' }
              }
            }} 
          />
          {isAuthenticated ? (
            <AuthUserButton />
          ) : (
            <Button 
              onClick={() => handleLogin(instance)} 
              variant="outlined"
              sx={{
                py: { xs: 0.5, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: '80px' },
                transition: 'all 0.2s ease',
              }}
            >
              {t('header.buttons.login', { ns: 'common' })}
            </Button>
          )}
        </Stack>
      </Stack>
    </Toolbar>
  );
};

export default Header;
